import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Summary from "components/Summary/Summary"
import useArticleDetailsBase from "./hooks/useArticleDetailsBase"
import { ArticleZts } from "models/ArticleZts"
import { ArticleEquipment } from "models/ArticleEquipment"
import useArticleTechnicalData from "hooks/useArticleTechnicalData"
import { IListItem, ListItemType } from "components/List/List"
import { ISummarySection } from "@/types"
import { getTranslation } from "utils/translations"
import { useTenantSettings } from "stores/tenantSettings"
import NoValue from "components/NoValue/NoValue"
import Features from "components/Features/Features"

interface IArticleDetailsEquipmentProps {
    article: ArticleEquipment | ArticleZts
    onEditInspectionSetting?: () => void
}

const ArticleDetailsEquipment: FC<IArticleDetailsEquipmentProps> = ({
    article,
    onEditInspectionSetting
}) => {
    const { t } = useTranslation()
    const detailsBase = useArticleDetailsBase({ article, onEditInspectionSetting })
    const { tenantPrimaryLanguageIsoCode } = useTenantSettings()

    const {
        technicalDataItems,
        technicalDataSubSections,
    } = useArticleTechnicalData({
        article,
    })

    const features = getTranslation(article.features, tenantPrimaryLanguageIsoCode)

    const details: ISummarySection<IListItem>[] = useMemo(() => {
        return [
            ...detailsBase,
            {
                title: t('article.technicalData'),
                items: technicalDataItems,
                subSections: technicalDataSubSections,
            },
            {
                title: t('features.features'),
                items: [{
                    type: ListItemType.verticalSection,
                    value: features
                        ? <Features features={features} />
                        : <NoValue>{t('features.noFeaturesAdded')}</NoValue>
                }],
            }
        ]
    }, [detailsBase, features, t, technicalDataItems, technicalDataSubSections])


    return (
        <Summary data={details} />
    )
}

export default ArticleDetailsEquipment
